import type { ServiceLineId } from './services'

export interface ArticleSection {
  heading?: string
  paragraphs: string[]
}

export interface ArticleSource {
  title: string
  url: string
}

export type ArticleFigure =
  | {
      kind: 'line'
      title: string
      caption: string
      note?: string
      xLabel: string
      yLabel: string
      yMin?: number
      yMax?: number
      points: { x: string; y: number; label?: string; reported?: boolean }[]
    }
  | {
      kind: 'bars'
      title: string
      caption: string
      note?: string
      max?: number
      bars: { label: string; value: number; display?: string }[]
    }
  | {
      kind: 'flow'
      title: string
      caption: string
      note?: string
      steps: { label: string; verb: string }[]
    }

export interface Article {
  slug: string
  title: string
  excerpt: string
  summaryBlock: string
  serviceLine: ServiceLineId
  sectors: string[]
  author: { name: string; role: string }
  heroImage: string
  heroAlt: string
  readTime: number
  publishedAt: string
  gated: boolean
  teaserCut: number
  gateHeadline: string
  gateValueLine: string
  featured?: boolean
  body: ArticleSection[]
  figure?: ArticleFigure
  sources: ArticleSource[]
}

const WORLD_BANK_DPTR: ArticleSource = {
  title: 'World Bank, Digital Progress and Trends Report 2025: Strengthening AI Foundations',
  url: 'https://www.worldbank.org/en/publication/dptr2025-ai-foundations',
}

const IFC_DIGITAL: ArticleSource = {
  title: 'IFC, Digital Opportunities in African Businesses, 2024',
  url: 'https://www.ifc.org/en/pressroom/2024/ifc-report-shows-digitalization-holds-immense-promise-economic-potential-for-african-businesses-of-all-sizes',
}

const DELOITTE_BOARDS: ArticleSource = {
  title: 'Deloitte Global, Governance of AI: A critical imperative for today’s boards, 2nd edition',
  url: 'https://www.deloitte.com/global/en/issues/trust/progress-on-ai-in-the-boardroom-but-room-to-accelerate.html',
}

export const ARTICLES: Article[] = [
  {
    slug: 'africa-ai-market-2030',
    title: 'The 16.5 billion dollar question: who captures Africa AI market by 2030',
    excerpt:
      'An Africa AI market forecast is only the starting point. The commercial question is who converts adoption into revenue, productivity and locally retained value.',
    summaryBlock:
      'An Africa AI market forecast is only the starting point. The commercial question is who converts adoption into revenue, productivity and locally retained value.',
    serviceLine: 'intelligence',
    sectors: ['Banking', 'Health', 'Agriculture'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/22690c3b-45da-41ff-a5a0-57ef08af3f8f.jpg',
    heroAlt: 'Navy and gold illustration of a bank, hospital, government building and warehouse connected to cloud infrastructure and a rising results chart',
    readTime: 5,
    publishedAt: '2026-07-14',
    gated: true,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine:
      'Read the full analysis, including where commercial demand is likely to form and who captures the value by 2030. We will also send you our fortnightly brief.',
    featured: true,
    body: [
      {
        heading: 'The number that matters, and the qualification behind it',
        paragraphs: [
          'Mastercard reported in August 2025 that Statista projected Africa’s artificial intelligence market to grow from US$4.5 billion in 2025 to US$16.5 billion in 2030. The two endpoints imply annualised growth of about 29.7 percent. The intermediate values in the chart are calculated from those endpoints. They are not annual observations published by Statista.',
          'That distinction matters because a market forecast is not revenue already captured by African companies. It is an estimate of the future value of AI related activity. The commercial question is therefore what organisations will actually buy, what suppliers will provide, and where the resulting economic value will sit.',
        ],
      },
      {
        heading: 'Adoption is not the same as value capture',
        paragraphs: [
          'IFC estimates that more than 600,000 formal businesses and 40 million microbusinesses in Africa could benefit from digitalisation. Yet fewer than one in three digitally engaged firms use technology intensively for business purposes. IFC also reports that 86 percent of African firms have access to digital tools such as mobile phones and internet connectivity.',
          'This creates a clear gap between access and productive use. AI can expand that market, but the opportunity is not limited to selling model access. It includes implementation, data preparation, workflow integration, sector specific software, cybersecurity, monitoring and local support.',
        ],
      },
      {
        heading: 'The foundations determine how fast the market can scale',
        paragraphs: [
          'The World Bank identifies four foundations for AI adoption: connectivity, compute, context and competency. Its 2025 report shows that 93 percent of people in high income countries use the internet compared with 54 percent in lower middle income countries and 27 percent in low income countries. It also reports that high income countries held 77 percent of global colocation data centre capacity as of June 2025.',
          'Those figures do not mean African organisations cannot use AI. They mean the architecture has to reflect local constraints. Smaller models, cloud services and AI applications designed for ordinary devices can lower the infrastructure threshold. The World Bank specifically identifies this category as small AI.',
        ],
      },
      {
        heading: 'Where the commercial demand is likely to form',
        paragraphs: [
          'Mastercard identifies agriculture, healthcare, education, energy and finance among the major sectors where AI can drive transformation. These sectors contain repeated decisions that can be measured, including credit assessment, fraud detection, customer service, forecasting, clinical support and resource allocation.',
          'The business case becomes stronger when the starting point is a measurable operating problem. A bank can measure fraud losses and processing time. A logistics company can measure delivery performance. A health provider can measure turnaround time. The AI system is then evaluated against an existing baseline rather than against a technology demonstration.',
        ],
      },
      {
        heading: 'The question for 2030',
        paragraphs: [
          'The projected US$16.5 billion market should therefore not be treated as a single prize. Software providers may capture subscription revenue. Local technology firms may capture implementation and product revenue. African banks, manufacturers, hospitals and public institutions may capture productivity gains. Customers may capture better service or lower costs.',
          'The organisations that create the most durable value will be those that connect AI to real workflows, build reliable data, train people to supervise the systems and design products around the economics of the markets they serve.',
          'The 2030 question is not simply whether Africa will use AI. It is where the revenue, productivity gains, capabilities and intellectual property will sit when the forecast market becomes actual spending.',
        ],
      },
    ],
    figure: {
      kind: 'line',
      title: 'Africa AI market: forecast endpoints and implied growth path',
      caption: 'Figure 1. Reported forecast endpoints with the implied annual growth path between them.',
      note: '2025 and 2030 are reported forecast endpoints. Intermediate values are mathematical interpolation, not reported market measurements.',
      xLabel: 'Year',
      yLabel: 'US$ billions',
      yMin: 4,
      yMax: 18,
      points: [
        { x: '2025', y: 4.5, label: '$4.5bn', reported: true },
        { x: '2026', y: 5.84 },
        { x: '2027', y: 7.57 },
        { x: '2028', y: 9.82 },
        { x: '2029', y: 12.73 },
        { x: '2030', y: 16.5, label: '$16.5bn', reported: true },
      ],
    },
    sources: [
      {
        title: 'Mastercard, Harnessing the Transformative Power of AI in Africa, 6 August 2025',
        url: 'https://www.mastercard.com/news/eemea/en/newsroom/press-releases/en/2025-1/august/ai-in-africa-to-top-16-5b-by-2030-mastercard-explores-path-for-continued-digital-transformation/',
      },
      WORLD_BANK_DPTR,
      IFC_DIGITAL,
    ],
  },
  {
    slug: 'ai-readiness-five-dimensions',
    title: 'Five dimensions that decide whether your AI programme survives contact with reality',
    excerpt:
      'An AI programme can have a strong model and still fail because the organisation cannot connect to it, feed it, govern it or use it.',
    summaryBlock:
      'An AI programme can have a strong model and still fail because the organisation cannot connect to it, feed it, govern it or use it.',
    serviceLine: 'ai',
    sectors: ['Government', 'Banking', 'Education'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/30ced27e-5a7d-4a96-81ad-a5565ffb71b8.jpg',
    heroAlt: 'Layered diagram of four stacked foundations enclosed by a single governance outline',
    readTime: 4,
    publishedAt: '2026-06-28',
    gated: true,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read all five dimensions and the assessment sequence we recommend before any build.',
    body: [
      {
        heading: '1. Connectivity',
        paragraphs: [
          'AI begins with reliable access to digital infrastructure. The World Bank reports internet use of 54 percent in lower middle income countries and 27 percent in low income countries, compared with 93 percent in high income countries. Connectivity also depends on electricity, devices, affordability and network quality.',
          'For an organisation, the test is more specific. The question is whether the people and systems involved in the target workflow can access the AI service consistently. If the answer changes by location, device or time of day, the technology is not yet operating on a stable foundation.',
        ],
      },
      {
        heading: '2. Compute',
        paragraphs: [
          'The second dimension is the processing capacity required to run AI. The World Bank reports that high income countries held 77 percent of global colocation data centre capacity in June 2025. Upper middle income countries held 18 percent, lower middle income countries 5 percent and low income countries less than 0.1 percent.',
          'Most organisations do not need to build a data centre. They do need to decide where computing will happen, what information leaves the organisation, what latency is required and how the cost scales with usage.',
        ],
      },
      {
        heading: '3. Context',
        paragraphs: [
          'AI models require relevant data and content. The World Bank calls this context and identifies it as one of the four foundations of AI adoption.',
          'Context includes customer records, policies, product definitions, local languages, historical decisions and sector terminology. A model can be technically capable while still producing weak operational results if it lacks the information that defines the organisation’s reality.',
        ],
      },
      {
        heading: '4. Competency',
        paragraphs: [
          'AI does not remove the need for skilled people. It changes the skills required around the workflow. The World Bank reports that fewer than 5 percent of people in low income countries have basic digital skills compared with 66 percent in high income countries.',
          'An AI programme therefore needs technical specialists, process owners and users who can judge outputs. The system may produce an answer in seconds, but an accountable person still needs to decide whether the answer is correct and appropriate.',
        ],
      },
      {
        heading: '5. Governance',
        paragraphs: [
          'Governance connects the other four dimensions to accountability. Deloitte’s 2025 survey of 695 board members and executives across 56 countries found that 31 percent said AI was not on the board agenda, 66 percent said their boards had limited to no knowledge or experience with AI, and 31 percent said their organisations were not ready to deploy AI.',
          'Governance should define ownership, approval thresholds, monitoring, escalation and the conditions under which an AI system is stopped. It turns AI from an experiment into an accountable operating system.',
        ],
      },
      {
        heading: 'The assessment sequence',
        paragraphs: [
          'Start with the business decision rather than the model. Identify the data and infrastructure required. Test connectivity and compute. Assess context and competency. Then define governance and monitoring.',
          'That sequence prevents an organisation from treating AI readiness as a procurement question. Readiness is an operating condition. The technology can be purchased quickly. The conditions that make it useful take longer to build.',
        ],
      },
    ],
    figure: {
      kind: 'bars',
      title: 'Selected AI foundation indicators',
      caption: 'Figure 1. Selected reported indicators behind the five dimensions. These figures are not a composite readiness score.',
      max: 100,
      bars: [
        { label: 'Basic digital skills, high income', value: 66 },
        { label: 'Basic digital skills, low income', value: 5 },
        { label: 'Global data centre capacity, middle and low income', value: 23 },
        { label: 'Internet use, low income', value: 27 },
        { label: 'Internet use, lower middle income', value: 54 },
      ],
    },
    sources: [
      WORLD_BANK_DPTR,
      {
        title: 'World Bank, Strengthening AI Foundations: Emerging Opportunities for Developing Countries',
        url: 'https://www.worldbank.org/en/news/factsheet/2025/11/21/strengthening-ai-foundations-emerging-opportunities-for-developing-countries',
      },
      DELOITTE_BOARDS,
    ],
  },
  {
    slug: 'go-to-market-african-markets',
    title: 'Why go to market plans built for one African market rarely travel to the next',
    excerpt:
      'Geographic proximity does not make markets commercially identical. Payment systems, channels, regulation and buyer behaviour can change before the border does.',
    summaryBlock:
      'Geographic proximity does not make markets commercially identical. Payment systems, channels, regulation and buyer behaviour can change before the border does.',
    serviceLine: 'growth',
    sectors: ['SMEs', 'Logistics', 'Telecoms'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/a18c183f-c4a0-47a6-a0ad-d515ca5a7855.jpg',
    heroAlt: 'Map based illustration of separate payment rails, distribution channels and customer journeys across Africa',
    readTime: 4,
    publishedAt: '2026-06-09',
    gated: true,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read the full piece, including the five local variables we map before every market entry.',
    body: [
      {
        heading: 'The gap is not access. It is intensity',
        paragraphs: [
          'IFC reports that 86 percent of African firms have access to digital tools such as mobile phones and internet connectivity. Yet fewer than one in three digitally engaged firms use technology intensively for business purposes.',
          'That difference is the starting point for expansion. A company can enter a market where customers are connected and still discover that the commercial workflow around the technology is different from the one it built in another country.',
        ],
      },
      {
        heading: 'Payments demonstrate local differences',
        paragraphs: [
          'GSMA reported that merchant payments through mobile money grew by almost half globally to US$155 billion in 2025. The scale confirms that digital payments are a major commercial channel, but it does not mean the same payment route dominates every market.',
          'Payment behaviour depends on the local mix of mobile money operators, banks, cards, agents, merchants, interoperability and regulation. A go to market plan that assumes one payment architecture can therefore fail even when the underlying product is unchanged.',
        ],
      },
      {
        heading: 'Distribution is part of the product',
        paragraphs: [
          'IFC research highlights the importance of informal businesses and fragmented distribution in African markets. Many businesses and consumers are reached through networks that are not equivalent to the online retail structures common in higher income markets.',
          'The practical question is therefore who reaches the customer. Depending on the market, the answer may be a distributor, bank, mobile money agent, marketplace, field sales team, wholesaler or community network.',
        ],
      },
      {
        heading: 'Why translation is not enough',
        paragraphs: [
          'When a company enters a second market, it often translates the first market’s sales material and changes the currency. That changes the language, not the operating model.',
          'A proper expansion process maps five local variables: target buyer, acquisition channel, payment method, regulatory requirement and fulfilment model. Each variable should be classified as transferable, locally dependent or unknown.',
          'The unknowns should be tested before the full launch. That turns expansion into a sequence of commercial experiments instead of a large assumption.',
        ],
      },
      {
        heading: 'The operating rule',
        paragraphs: [
          'The original proposition can remain consistent while the route to market changes. That is the distinction between a regional product and a copied country plan.',
          'The useful measure is not how quickly a company reproduces its first market. It is how quickly the company identifies which assumptions do not travel and changes them before they become expensive.',
        ],
      },
    ],
    figure: {
      kind: 'bars',
      title: 'African business digitalisation gap',
      caption: 'Figure 1. IFC evidence on the difference between digital access and intensive business use.',
      max: 100,
      bars: [
        { label: 'Firms with access to digital tools', value: 86 },
        { label: 'Digitally engaged firms using technology intensively', value: 32, display: 'Under 33%' },
      ],
    },
    sources: [
      IFC_DIGITAL,
      { title: 'GSMA, State of the Industry Report on Mobile Money 2025', url: 'https://www.gsma.com/sotir/' },
      {
        title: 'GSMA, SOTIR 2025 global deep dive and regional cuts',
        url: 'https://www.gsma.com/solutions-and-impact/connectivity-for-good/mobile-for-development/gsma_resources/the-sotir-2025-global-deep-dive-and-regional-cuts/',
      },
    ],
  },
  {
    slug: 'data-foundations-before-ai',
    title: 'The unglamorous work that decides whether AI pays for itself',
    excerpt:
      'AI value begins before the model. It begins with definitions, ownership, data quality and a process that can be measured.',
    summaryBlock:
      'AI value begins before the model. It begins with definitions, ownership, data quality and a process that can be measured.',
    serviceLine: 'ai',
    sectors: ['Health', 'Logistics', 'Banking'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/106e8e43-9714-44dd-a7b5-1d87b26325f9.jpg',
    heroAlt: 'Data pipeline illustration where raw records pass through definition, ownership, quality and access gates to a single decision',
    readTime: 4,
    publishedAt: '2026-05-21',
    gated: false,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read the rest of this analysis.',
    body: [
      {
        heading: 'AI inherits the quality of the organisation underneath it',
        paragraphs: [
          'IFC reports that 86 percent of African firms have access to basic digital tools, while fewer than one in three digitally engaged firms use technology intensively for business purposes.',
          'That gap matters because AI is layered on top of existing information systems. If customer records are inconsistent, product definitions differ between departments or key decisions exist only in unstructured documents, an AI system inherits those weaknesses.',
        ],
      },
      {
        heading: 'Context requires definitions',
        paragraphs: [
          'The World Bank identifies context as one of the four foundations of effective AI adoption. Context includes the data and content that allow a model or application to operate within a specific economic, cultural and institutional environment.',
          'Before automation, the organisation needs definitions. What counts as a customer? What counts as a completed transaction? Which product name is official? Which policy version is current? These questions sound administrative, but they determine whether an automated answer can be trusted.',
        ],
      },
      {
        heading: 'Ownership prevents data from becoming everyone’s problem',
        paragraphs: [
          'Every critical dataset needs an accountable owner. Ownership means responsibility for definitions, access, quality and change management.',
          'The owner does not have to write code. The owner has to be able to answer whether the dataset is fit for a particular business decision and what should happen when its quality falls below the required level.',
        ],
      },
      {
        heading: 'Quality checks belong inside the workflow',
        paragraphs: [
          'Useful controls include completeness, validity, duplication, consistency, timeliness and unusual changes in distribution. The exact threshold depends on the decision.',
          'A missing address may not matter for a general marketing analysis. It may matter significantly for a delivery optimisation system. Data quality therefore has to be assessed against the use case rather than treated as a universal percentage.',
        ],
      },
      {
        heading: 'Measure the business result',
        paragraphs: [
          'The purpose of data preparation is not to produce a clean spreadsheet. It is to improve a decision.',
          'Start with one operational decision. Map the data. Assign ownership. Measure quality. Correct the highest impact problems. Deploy the AI workflow. Then compare the result with the original baseline.',
          'This creates a chain from data to decision to measurable outcome. It also creates a stopping rule. If better data does not improve the decision, the organisation has evidence that the proposed AI use case needs to change.',
        ],
      },
      {
        heading: 'The part that gets cut first',
        paragraphs: [
          'Data work is often less visible than a model demonstration, so it is easy to remove from an AI budget. The economic consequence is that the organisation pays for automation while retaining the manual reconciliation that automation was supposed to remove.',
          'The unglamorous work is therefore part of the AI business case. Definitions, ownership and quality controls are not preparation for the value. They are part of the mechanism that creates it.',
        ],
      },
    ],
    figure: {
      kind: 'flow',
      title: 'Data foundation before AI deployment',
      caption: 'Figure 1. A practical data foundation sequence from definitions to measured business outcomes.',
      note: 'The operating chain: define, own, test, control, measure.',
      steps: [
        { label: 'Data definitions', verb: 'Define' },
        { label: 'Ownership', verb: 'Own' },
        { label: 'Quality checks', verb: 'Test' },
        { label: 'Access control', verb: 'Control' },
        { label: 'Measurement', verb: 'Measure' },
      ],
    },
    sources: [
      WORLD_BANK_DPTR,
      IFC_DIGITAL,
      {
        title: 'OECD, AI Governance in Africa, 2026',
        url: 'https://www.oecd.org/en/publications/oecd-artificial-intelligence-case-studies_c517fcf5-en/ai-governance-in-africa_1ff55135-en.html',
      },
    ],
  },
  {
    slug: 'boards-asking-better-ai-questions',
    title: 'Six questions a board should ask before approving an AI budget',
    excerpt:
      'An AI budget should be approved as an operating investment with measurable outcomes and controls, not simply as a technology category.',
    summaryBlock:
      'An AI budget should be approved as an operating investment with measurable outcomes and controls, not simply as a technology category.',
    serviceLine: 'ai',
    sectors: ['Government', 'NGOs', 'Banking'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/ea503317-beac-45aa-b86a-9b96f441ea5c.jpg',
    heroAlt: 'Top down boardroom illustration with six evidence cards laid across the table',
    readTime: 4,
    publishedAt: '2026-05-02',
    gated: true,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read all six questions and what the board’s role should be before capital is committed.',
    body: [
      {
        heading: '1. What decision are we improving?',
        paragraphs: [
          'A budget request should identify the decision or workflow before it identifies the AI technology. Management should be able to state what changes, who uses the output and which operating measure will move.',
          'Without that baseline, an AI proposal can become a technology purchase without a defined business result.',
        ],
      },
      {
        heading: '2. What evidence will prove value?',
        paragraphs: [
          'The board should require a baseline, target and measurement period. The target should be expressed in business terms such as processing time, cost, error rate, revenue, customer response time or loss rate.',
          'Model accuracy can be useful, but it is not the same as business value. A highly accurate model has little economic effect if employees do not use it.',
        ],
      },
      {
        heading: '3. Who owns the system after launch?',
        paragraphs: [
          'Ownership should cover the product, data, risk, budget and operating process. Someone should have authority to approve changes and someone should have authority to stop the system.',
          'Deloitte’s 2025 survey of 695 board members and executives across 56 countries found that 31 percent said AI was not on the board agenda and 66 percent said their boards had limited to no knowledge or experience with AI.',
        ],
      },
      {
        heading: '4. What are the failure conditions?',
        paragraphs: [
          'NACD guidance asks directors to consider AI risk appetite, testing, monitoring, auditing, employee training, supplier due diligence and model drift.',
          'The board should therefore ask what happens when the model is wrong, unavailable, manipulated or used outside its intended context. The answer should identify human escalation, logging, monitoring thresholds and a process for suspension.',
        ],
      },
      {
        heading: '5. What data and supplier dependencies are being funded?',
        paragraphs: [
          'The cost of AI can include data preparation, integration, cloud infrastructure, security, monitoring, training and external suppliers.',
          'The World Bank’s four foundations are useful here: connectivity, compute, context and competency. A budget that covers only model access can leave the organisation unable to operate the system reliably.',
        ],
      },
      {
        heading: '6. What happens if the forecast is wrong?',
        paragraphs: [
          'Every AI business case contains assumptions. The board should ask which assumptions matter most, how they will be tested and what spending can be stopped if the evidence changes.',
          'Funding can then be released in stages as the organisation proves data quality, adoption, operating performance and risk controls.',
        ],
      },
      {
        heading: 'The board’s role',
        paragraphs: [
          'Board oversight does not require every director to become a machine learning specialist. It requires directors to interrogate the objective, evidence, ownership, dependencies, failure conditions and measurement plan.',
          'The value of these questions is not that they guarantee a successful AI programme. They make the assumptions visible before capital is committed.',
        ],
      },
    ],
    figure: {
      kind: 'bars',
      title: 'AI oversight findings from Deloitte’s 2025 survey',
      caption: 'Figure 1. Selected findings from Deloitte’s 2025 global board and executive survey.',
      max: 100,
      bars: [
        { label: 'AI not on the board agenda', value: 31 },
        { label: 'Boards with limited or no AI knowledge', value: 66 },
        { label: 'Organisations not ready to deploy AI', value: 31 },
      ],
    },
    sources: [
      DELOITTE_BOARDS,
      {
        title: 'NACD, Boardroom Tool: Questions for Directors to Ask About AI',
        url: 'https://prod.nacdonline.org/all-governance/governance-resources/governance-research/director-handbooks/DH/2025/ai-in-cybersecurity/boardroom-tool-questions-for-directors-to-ask-about-ai/',
      },
      WORLD_BANK_DPTR,
    ],
  },
  {
    slug: 'pricing-under-currency-pressure',
    title: 'Holding your pricing when the currency will not hold still',
    excerpt:
      'Currency movement reaches the customer through input costs, replacement costs, working capital and the price required to maintain margin.',
    summaryBlock:
      'Currency movement reaches the customer through input costs, replacement costs, working capital and the price required to maintain margin.',
    serviceLine: 'growth',
    sectors: ['SMEs', 'Logistics', 'Agriculture'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/15b4ff91-48de-4aa8-a7b7-8c4c3c8d0c58.jpg',
    heroAlt: 'Pricing control room illustration linking exchange rate, inventory, margin, quote validity and replacement cost to one price decision',
    readTime: 4,
    publishedAt: '2026-04-16',
    gated: true,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read the three pricing structures for unstable costs and the management rule behind them.',
    body: [
      {
        heading: 'The exposure starts with the cost base',
        paragraphs: [
          'If a business earns revenue in local currency but buys inputs in dollars or another foreign currency, depreciation changes the local currency cost of the same input.',
          'A US$100 input therefore requires more local currency after a depreciation. The chart is an illustrative sensitivity, not a forecast for any specific African currency.',
        ],
      },
      {
        heading: 'Africa does not have one currency story',
        paragraphs: [
          'The African Development Bank estimated average inflation across Africa at 13.6 percent in 2025, down from 21.8 percent in 2024. It also reported that 28 African currencies significantly strengthened against the US dollar in 2025 while depreciation slowed in others.',
          'The World Bank reported that the median inflation rate in Sub Saharan Africa declined from 7.1 percent in 2023 to 4.5 percent in 2024 and projected 4.6 percent for 2025 to 2027.',
          'These regional figures do not remove country level differences. A pricing system therefore has to respond to the business’s own cost and currency exposure rather than to an Africa wide average.',
        ],
      },
      {
        heading: 'Why static pricing loses information',
        paragraphs: [
          'A price set once in local currency assumes the cost base will remain stable. When imported inputs, fuel, financing or replacement inventory move, that assumption becomes false.',
          'The business then has to absorb the cost, increase the price or delay the adjustment. Each choice affects margin, demand or working capital.',
        ],
      },
      {
        heading: 'Three structures for unstable costs',
        paragraphs: [
          'First, define a review trigger. A price review can be activated when the exchange rate or a major input crosses a predetermined threshold.',
          'Second, separate stable and variable components. A contract can keep the local operating component fixed while linking the foreign currency component to a transparent reference.',
          'Third, shorten quote validity when replacement costs move quickly. A defined expiry date is clearer than an unexplained price change after the customer has already committed.',
        ],
      },
      {
        heading: 'Working capital is part of the price',
        paragraphs: [
          'Currency movement affects the amount of cash required to replace inventory. If the business collects payment after the replacement cost has risen, the original margin may not finance the next purchase.',
          'MIT research on currency volatility in Africa notes that persistent depreciation has put pressure on SMEs and their access to finance.',
          'Pricing therefore needs to connect selling price, replacement cost, collection period and currency exposure. Profit on the original invoice is not enough if the next inventory cycle requires more cash than the business has.',
        ],
      },
      {
        heading: 'The management rule',
        paragraphs: [
          'The relevant question is not only whether the currency moved. It is whether the business can replace its inputs, service its foreign currency obligations and maintain its target margin at the new rate.',
          'A disciplined pricing model does not predict the exchange rate. It defines what the business will do when the exchange rate changes.',
        ],
      },
    ],
    figure: {
      kind: 'line',
      title: 'Illustrative local currency cost of a US$100 input',
      caption: 'Figure 1. Illustrative sensitivity of a US$100 input to local currency depreciation.',
      note: 'Illustrative sensitivity. A 10% depreciation makes a US$100 input cost 10% more in local currency, before other price effects.',
      xLabel: 'Local currency depreciation against USD',
      yLabel: 'Local currency cost index',
      yMin: 95,
      yMax: 135,
      points: [
        { x: '0%', y: 100, label: '100' },
        { x: '5%', y: 105 },
        { x: '10%', y: 110, label: '110' },
        { x: '15%', y: 115 },
        { x: '20%', y: 120, label: '120' },
        { x: '25%', y: 125 },
        { x: '30%', y: 130, label: '130' },
      ],
    },
    sources: [
      {
        title: 'African Development Bank, 2026 economic commentary on African inflation and currencies',
        url: 'https://www.afdb.org/en/news-and-events/speeches/speech-delivered-prof-kevin-chika-urama-faas-chief-economist-and-vice-president-african-development-bank-group-official-launch-joint-policy-paper-impacts-middle-east-conflict-african-economies-92591',
      },
      { title: 'World Bank, Africa Pulse, April 2025', url: 'https://www.worldbank.org/en/publication/africa-pulse/april-2025' },
      {
        title: 'MIT Kuo Sharper Center for Prosperity and Entrepreneurship, Hedging Currency Risk: From Strategies to Implementation, 2025',
        url: 'https://mitsloan.mit.edu/centers-initiatives/ksc/hedging-currency-risk-strategies-to-implementation',
      },
    ],
  },
]

export function getArticle(slug: string): Article | undefined {
  return ARTICLES.find((article) => article.slug === slug)
}
